import { Component } from 'shimmer'

import { Vector3, Quaternion, MirroredRepeatWrapping } from 'three'
import { Loader } from '@/utils/Loader'

import { randomRange } from '@/utils/Maths'

import gsap from 'gsap'

const hitDuration = 1000
const distFactor = .01

export class Letter extends Component {
  constructor (mesh, data, getIntersectPosition) {
    super('Letter')

    this.onUp = this.onUp.bind(this)
    this.onDown = this.onDown.bind(this)
    this.onPointermove = this.onPointermove.bind(this)
    this.onDestroy = this.onDestroy.bind(this)
    this.setEnvMap = this.setEnvMap.bind(this)

    this.basePosition = new Vector3(randomRange(-.5, .5), .4, randomRange(-.5, .5))
    this.baseRotation = new Vector3(-Math.PI * .5, randomRange(-.2, .2), randomRange(-.2, .2))

    // this.object = mesh
    this.targetPosition = new Vector3(data.position.x, data.position.y, data.position.z)
    if (data.rotation) {
      this.targetRotation = new Vector3(data.rotation.x * Math.PI - Math.PI * .5, data.rotation.y * Math.PI, data.rotation.z * Math.PI)
    }
    else {
      this.targetRotation = new Vector3(Math.random() * Math.PI * 2, Math.random() * Math.PI * 2, Math.random() * Math.PI * 2)
    }
    this.getIntersectPosition = getIntersectPosition
    this.isUp = false
    this.progress = 0
    this.isPrimitive = !!mesh

    if (mesh) {
      this.promise = new Promise(resolve => {
        this.object = mesh
        const scale = randomRange(.1, .15)
        this.object.scale.set(scale, scale, scale)
        this.object.receiveShadow = true
        this.object.castShadow = true
        this.object.position.copy(this.basePosition)
        this.object.rotation.x = this.baseRotation.x
        this.object.rotation.y = this.baseRotation.y
        this.object.rotation.z = this.baseRotation.z
        this.add(this.object)
        resolve()
      })
    }
    else {
      this.promise = Loader.load(data.url, false, obj => {
        obj.receiveShadow = true
        obj.castShadow = true
      }).then(obj => {
        this.object = obj
        this.object.position.copy(this.basePosition)
        this.object.rotation.x = this.baseRotation.x
        this.object.rotation.y = this.baseRotation.y
        this.object.rotation.z = this.baseRotation.z
        this.add(this.object)
      })
    }

    this.elapsed = 0
    this.touches = []
  }

  onUp () {
    this.isUp = true
    this.ease = gsap.to(this.object.position, {
      x: this.targetPosition.x,
      y: this.targetPosition.y,
      z: this.targetPosition.z,
      duration: 2,
      ease: 'power4.inOut',
      onUpdate: () => {
        this.progress = this.ease.progress()
      }
    })

    gsap.to(this.object.rotation, {
      x: this.targetRotation.x,
      y: this.targetRotation.y,
      z: this.targetRotation.z,
      duration: 2,
      ease: 'power4.inOut'
    })
  }

  onDestroy () {
    this.object.traverse(child => {
      if (child.isMesh) {
        child.material.dispose()
        child.geometry.dispose()
      }
    })
    this.destroy()
  }

  onDown () {
    this.isUp = false
    gsap.to(this.object.position, {
      x: this.basePosition.x,
      y: this.basePosition.y,
      z: this.basePosition.z,
      duration: 2,
      ease: 'power4.inOut',
      onUpdate: () => {
        this.progress = 1 - this.ease.progress()
      }
    })

    gsap.to(this.object.rotation, {
      x: this.baseRotation.x,
      y: this.baseRotation.y,
      z: this.baseRotation.z,
      duration: 2,
      ease: 'power4.inOut'
    })
  }

  setMap (texture) {
    if (this.isPrimitive) { return }

    if (this.object.material) {
      this.object.material.map = texture
    }
    this.object.children.forEach( obj => {
      obj.material.map = texture
      obj.material.specular *= .8
      obj.material.roughness *= 1.1
      obj.material.metalness *= .6
    })
  }

  setEnvMap (texture) {
    if (this.object.material) {
      this.object.material.envMap = texture
    }
    this.object.children.forEach( obj => {
      obj.material.envMap = texture
      obj.material.envMapIntensity = .5
    })
  }

  onPointermove (delta) {
    if (!this.object || !this.isUp) { return }

    const intersections = this.getIntersectPosition(this.object)

    if (intersections && (!this.touches.length || this.touches[this.touches.length - 1].age > 200)) {
      this.touches.push({
        vector: {...delta},
        age: 0
      })
    }
  }

  onUpdate ({ delta }) {
    if (this.touches.length) {
      const framePosition = this.targetPosition.clone()
      const frameRotation = this.targetRotation.clone()

      for (var i = this.touches.length - 1; i >= 0; i--) {
        const touch = this.touches[i]
        touch.age += delta
        if (touch.age >= hitDuration) {
          this.touches.splice(i, 1)
          continue
        }
        const strengthFactor = Math.sin(touch.age * .01)
        // function y = 1/x
        const oldFactor = (1 / ((touch.age*.001)+1))
        // function y = -x + 1
        const linearOldFactor = touch.age * ( -1 / hitDuration ) + 1

        const scalar = strengthFactor * oldFactor * linearOldFactor
        const vecPosition = new Vector3( -touch.vector.x, touch.vector.y, 0 ).multiplyScalar(scalar * distFactor)
        const vecRotation = new Vector3( -touch.vector.y, 0, touch.vector.x ).multiplyScalar(scalar * .015)

        framePosition.add( vecPosition )
        frameRotation.add( vecRotation )
      }

      this.object.position.copy(framePosition)
      this.object.rotation.setFromVector3(frameRotation, 'XYZ')
    }
  }
}